import { Customer } from '@app/domain/Customer'
import { CustomerDTO } from '@services/DTO/CustomerDTO'
import { DTOAdapter } from './DTOAdapter'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable, map } from 'rxjs'
import { Page } from '@app/domain/Page'
import { PageDTO } from '@services/DTO/PageDTO'
import { compare } from 'fast-json-patch'
import { environment } from '../../environments/environment'

export type SortColumn = 'firstName' | 'lastName' | 'email'
export type SortDirection = 'asc' | 'desc'

export interface SortConfig {
    column: SortColumn | null
    direction: SortDirection
}

export interface MergeCustomerProfilesRequest {
    customerIds: string[]
    firstName: string | null
    lastName: string | null
    emailAddress: string | null
    phoneNumber: string | null
    emailAddressAliases: string[]
    notes: string[]
    foodDrinkPreferences: string | null
    vip: boolean
    barred: boolean
    sendFeedbackRequest: boolean
    cardDetailsNotRequired: boolean
    depositAmount: number | null
}

interface CustomerDuplicatePairDTO {
    id: string
    first: CustomerDTO
    second: CustomerDTO
    similarity: number
}

export interface CustomerDuplicatePair {
    id: string
    first: Customer
    second: Customer
    similarity: number
}

@Injectable({
    providedIn: 'root',
})
export class CustomerService {

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter
    ) { }

    listCustomers(
        organisationId: string,
        businessId: string,
        venueId: string,
        page: number,
        pageSize: number,
        sort: SortConfig | null,
        searchTerm?: string,
        vipOnly?: boolean,
        barredOnly?: boolean,
    ): Observable<Page<Customer>> {
        const path =
            `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            '/customer/list'
        const url = new URL(path, environment.apiBaseURL)
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString())
        
        if (sort?.column) {
            params = params.set('sortBy', sort.column)
            params = params.set('direction', sort.direction)
        }
        if (searchTerm) {
            params = params.set('searchTerm', searchTerm)
        }
        if (vipOnly === true) {
            params = params.set('vipOnly', 'true')
        }
        if (barredOnly === true) {
            params = params.set('barred', 'true')
        }
        
        return this.http.get<PageDTO<CustomerDTO>>(url.toString(), { params })
            .pipe(
                map(pageDto => ({
                    items: pageDto.items.map(dto => this.dtoAdapter.adaptCustomerDto(dto)),
                    page: pageDto.page,
                    pageSize: pageDto.pageSize,
                    direction: pageDto.direction,
                    totalItems: pageDto.totalItems,
                }))
            )
    }

    listDuplicateCustomers(
        organisationId: string,
        businessId: string,
        venueId: string,
        page: number,
        pageSize: number,
        searchTerm?: string,
    ): Observable<Page<CustomerDuplicatePair>> {
        const path =
            `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            '/customer/duplicates'
        const url = new URL(path, environment.apiBaseURL)
        let params = new HttpParams()
            .set('page', page.toString())
            .set('pageSize', pageSize.toString())
        if (searchTerm) {
            params = params.set('searchTerm', searchTerm)
        }
        return this.http.get<PageDTO<CustomerDuplicatePairDTO>>(url.toString(), { params })
            .pipe(
                map(pageDto => ({
                    items: pageDto.items.map(pair => ({
                        id: pair.id,
                        first: this.dtoAdapter.adaptCustomerDto(pair.first),
                        second: this.dtoAdapter.adaptCustomerDto(pair.second),
                        similarity: pair.similarity,
                    })),
                    page: pageDto.page,
                    pageSize: pageDto.pageSize,
                    totalItems: pageDto.totalItems,
                }))
            )
    }

    searchCustomers(
        organisationId: string,
        businessId: string,
        venueId: string,
        firstName: string | null,
        lastName: string | null,
        emailAddress: string | null,
        phoneNumber: string | null
    ): Observable<Customer[]> {
        const path =
            `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            '/customer/search'
        const url = new URL(path, environment.apiBaseURL)
        let params = new HttpParams()
        if (firstName) {
            params = params.set('firstName', firstName)
        }
        if (lastName) {
            params = params.set('lastName', lastName)
        }
        if (emailAddress) {
            params = params.set('emailAddress', emailAddress)
        }
        if (phoneNumber) {
            params = params.set('phoneNumber', phoneNumber)
        }
        return this.http.get<CustomerDTO[]>(url.toString(), { params })
            .pipe(
                map(dtos => dtos.map(dto => this.dtoAdapter.adaptCustomerDto(dto)))
            )
    }

    searchCustomersBySearchTerm(
        organisationId: string,
        businessId: string,
        venueId: string,
        term: string
    ): Observable<Customer[]> {
        const path =
            `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/customer/search/${term}`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.get<CustomerDTO[]>(url.toString())
            .pipe(
                map(dtos => dtos.map(dto => this.dtoAdapter.adaptCustomerDto(dto)))
            )
    }

    updateCustomer(
        organisationId: string,
        businessId: string,
        venueId: string,
        customer: Customer,
        updatedCustomer: Customer
    ): Observable<Customer> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            `/customer/${customer.id}`
        const url = new URL(path, environment.apiBaseURL)
        const customerDTO = this.dtoAdapter.adaptCustomer(customer)
        const updatedCustomerDTO = this.dtoAdapter.adaptCustomer(updatedCustomer)
        const body = compare(customerDTO, updatedCustomerDTO)
        return this.http.patch<CustomerDTO>(url.toString(), body)
            .pipe(
                map(dto => this.dtoAdapter.adaptCustomerDto(dto))
            )
    }

    mergeCustomerProfiles(
        organisationId: string,
        businessId: string,
        venueId: string,
        request: MergeCustomerProfilesRequest
    ): Observable<Customer> {
        const path = `/organisation/${organisationId}` +
            `/business/${businessId}` +
            `/venue/${venueId}` +
            '/customer/merge'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.post<CustomerDTO>(url.toString(), request)
            .pipe(
                map(dto => this.dtoAdapter.adaptCustomerDto(dto))
            )
    }
}
